// @flow
import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import * as R from 'ramda';
import {
  DateTime,
} from 'luxon';

import type {
  Dispatch,
} from 'chronos-types';

import * as selectors from 'chronos-selectors';
import * as actions from 'chronos-actions';
import {
  jiraApi,
  chronosApi,
} from 'chronos-api';

import Spinner from '@atlaskit/spinner';
import ModalDialog from '@atlaskit/modal-dialog';

import {
  Flex,
} from 'chronos-styled';

import ActivityReport from './ActivityReport';


const mapState = (state) => {
  const worklogIds = selectors.getUiState('screenshotsViewerWorklogIds')(state);
  const worklogs = (
    worklogIds.map(
      id => (
        selectors.getResourceItemBydId('worklogs', id)(state)
      ),
    )
  );
  const issueIds = (
    worklogs.reduce(
      (acc, worklog) => ([
        ...acc,
        ...(
          acc.includes(worklog.issueId)
            ? []
            : [worklog.issueId]
        ),
      ]),
      [],
    )
  );
  const issues = (
    issueIds.map(
      id => (
        selectors.getResourceItemBydId('issues', id)(state)
      ),
    )
  );
  const worklogsIssueMap = (
    worklogs.reduce(
      (acc, worklog) => ({
        ...acc,
        [worklog.issueId]: [
          ...(
            acc[worklog.issueId] || []
          ),
          worklog,
        ],
      }),
      {},
    )
  );
  return ({
    isOpen: selectors.getUiState('screenshotsViewerModalIsOpen')(state),
    jiraType: selectors.getUiState('jiraType')(state),
    jiraUrl: selectors.getUiState('jiraUrl')(state),
    worklogIds,
    worklogs,
    worklogsIssueMap,
    issues,
  });
};

const Body = React.forwardRef((props: { children: Node }, ref) => (
  <Flex width="100%" borderRadius="4px" innerRef={ref} style={{ overflow: 'hidden' }}>
    {props.children}
  </Flex>
));

// eslint-disable-next-line react/no-multi-comp
const Container = React.forwardRef((props: { children: Node }, ref) => (
  <Flex borderRadius="4px" innerRef={ref} background="white">
    {props.children}
  </Flex>
));

const ScreenshotsViewerModal: React.StatelessFunctionalComponent<{}> = (): React.Node => {
  const {
    isOpen,
    jiraType,
    jiraUrl,
    worklogIds,
    worklogsIssueMap,
    issues,
  } = useSelector(mapState);
  const [activity, setActivity] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const dispatch: Dispatch = useDispatch();

  useEffect(
    () => {
      const fetchData = async () => {
        setLoading(true);
        let serverSettings = {};
        if (jiraType === 'server') {
          try {
            serverSettings = await jiraApi.getServerSettings();
          } catch (err) {
            console.log(err);
          }
        }
        const result = await (
          jiraType === 'server'
            ? jiraApi.getWorklogActivity
            : chronosApi.getScreenshots
        )({
          body: {
            worklogIds,
            issueId: "",
          }
        });
        const screenshotsWorlogMap = (
          result.worklogs.reduce(
            (acc, w) => ({
              ...acc,
              [w.worklogId]: ({
                ...w,
                screenshots: (
                  w.screenshots.map(
                    s => (
                      jiraType !== 'server'
                      || (serverSettings.screenshotsStorageSource === 's3')
                        ? s : ({
                          ...s,
                          imgUrl: `${jiraUrl}/plugins/servlet/chronos/screenshots/${s.filename}`,
                          thumbUrl: `${jiraUrl}/plugins/servlet/chronos/screenshots/${s.filenameThumb}`,
                        })
                    ),
                  )
                ),
              }),
            }),
            {},
          )
        );
        const newActivity = (
          issues.map(
            issue => ({
              id: issue.id,
              issue,
              worklogs: (
                worklogsIssueMap[issue.id].map(
                  (w) => {
                    const {
                      screenshots,
                      screenshotsPeriod,
                    } = screenshotsWorlogMap[w.id] || {
                      screenshots: [],
                      screenshotsPeriod: 600,
                    };
                    const createdFormat = (
                      DateTime.fromJSDate(
                        new Date(w.created),
                        {
                          setZone: true,
                        },
                      ).toFormat('yyyy/MM/dd t')
                    );
                    const [
                      firstScreenshot,
                      lastScreenshot,
                    ] = [R.head(screenshots), R.last(screenshots)];
                    if (firstScreenshot) {
                      const worklogStartedAt = DateTime.fromMillis(firstScreenshot.timestamp);
                      const worklogCompletedAt = (
                        DateTime.fromMillis(lastScreenshot.timestamp)
                      );
                      const hoursElapsed = (
                        screenshots.length === 1
                          ? 1
                          : Math.ceil(worklogCompletedAt.diff(worklogStartedAt).as('hours'))
                      );
                      const screenshotsPerHour = Math.ceil(3600 / screenshotsPeriod);
                      const sections = R.map(
                        s => ({
                          id: s,
                          sectionScreenshots: R.slice(
                            s * screenshotsPerHour,
                            ((s + 1) * screenshotsPerHour) + 1,
                            screenshots,
                          ),
                          startedAt: worklogStartedAt.plus({ hours: s }),
                        }),
                        R.range(0, hoursElapsed),
                      );
                      return {
                        ...w,
                        createdFormat,
                        sections,
                      };
                    }
                    return {
                      ...w,
                      createdFormat,
                      sections: [],
                    };
                  },
                )
              ),
            }),
          )
        );
        setActivity(newActivity);
        setLoading(false);
      };

      if (isOpen) {
        fetchData();
      } else {
        setActivity([]);
        setLoading(false);
      }
    },
    [isOpen],
  );

  const onDeleteScreenshot = useCallback(
    ({ worklog, screenshot }) => {
      const saveData = async () => {
        await (
          jiraType === 'server'
            ? jiraApi.deleteScreenshot
            : chronosApi.deleteScreenshot
        )({
          body: {
            worklogId: `'${worklog.id}'`,
            timestamp: screenshot.timestamp,
          },
        });
      };
      saveData();
      setActivity(
        activity.map(
          a => ({
            ...a,
            worklogs: (
              a.worklogs.map(
                w => (
                  worklog.id === w.id
                    ? ({
                      ...w,
                      sections: (
                        w.sections.map(
                          ws => ({
                            ...ws,
                            sectionScreenshots: (
                              ws.sectionScreenshots.map(
                                s => (
                                  s.timestamp === screenshot.timestamp
                                    ? ({
                                      ...s,
                                      status: 'deleted',
                                    }) : s
                                ),
                              )
                            ),
                          }),
                        )
                      ),
                    }) : w
                ),
              )
            ),
          }),
        ),
      );
    },
    [activity],
  );

  return isOpen && (
    <ModalDialog
      width="x-large"
      components={{
        Body,
        Container,
      }}
      onClose={() => {
        dispatch(actions.setUiState({
          screenshotsViewerModalIsOpen: false,
          screenshotsViewerWorklogIds: [],
        }));
      }}
    >
      <Flex width="100%" style={{ minHeight: 360 }}>
        {!isLoading
          ? (
            <Flex
              fillSpace
              scroll
            >
              <Flex
                column
                width="100%"
                spacing={16}
              >
                {activity.map(
                  a => (
                    <ActivityReport
                      key={a.id}
                      activity={a}
                      onDeleteScreenshot={onDeleteScreenshot}
                    />
                  ),
                )}
              </Flex>
            </Flex>
          ) : (
            <Flex
              style={{ minHeight: 360 }}
              column
              width="100%"
              height="100%"
              align="center"
              justify="center"
            >
              <Spinner size="xlarge" />
            </Flex>
          )
        }
      </Flex>
    </ModalDialog>
  );
};

export default ScreenshotsViewerModal;
